import React, {useCallback, useEffect, useState} from "react";
import {Alert, DatePicker, Spin} from "antd";
import {message} from "../../utils/antdFeedback";
import {DownloadOutlined, ReloadOutlined} from "@ant-design/icons";
import {LogIn, ArrowLeftRight, Coins, Landmark, Users, Calendar, Clock} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {AppDispatch, RootState} from "../../redux/store";
import {getGroupSavings} from "../../redux/slice/groupSavingsSlice";
import {getPaymentRequests} from "../../redux/slice/liquidationSlice";
import {getTransactions} from "../../redux/slice/transactionSlice";
import {getDashboardCards} from "../../redux/slice/dashboardSlice";
import {adminDownload, buildQuery} from "../../api/adminApi";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../auth/adminPermissions";
import {Card, COLORS, formatNaira} from "../../designSystem";

const formatNumber = (value: number | undefined) => (value || 0).toLocaleString();

const formatMoney = (value: number | undefined) => formatNaira(value);

const {RangePicker} = DatePicker;

/**
 * Money split into its whole and fractional parts so the hero figure can print the kobo
 * smaller — at display size the decimals are noise that crowds out the number that matters.
 */
const splitMoney = (value: number | undefined) => {
  const [whole, fraction] = Math.abs(Number(value) || 0).toFixed(2).split(".");
  return {whole: `₦${Number(whole).toLocaleString()}`, fraction};
};

const share = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);

const percentText = (value: number) => `${value.toFixed(value >= 10 ? 0 : 1)}%`;

const formatDate = (value: string | undefined) => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const getMetric = (source: any, camelKey: string, pascalKey: string) => {
  return source?.[camelKey] ?? source?.[pascalKey] ?? 0;
};

const toApiDate = (value: any) => value?.format?.("YYYY-MM-DD");

/* ── Presentation pieces, local to the dashboard ───────────────────────────── */

/** Small tinted chip. Carries a number beside the figure it qualifies. */
function Pill({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "brand" | "success" | "muted";
}) {
  const palette = {
    neutral: {background: "#F1F0EF", color: COLORS.text},
    brand: {background: COLORS.brandTint, color: COLORS.brand},
    success: {background: "#DCFCE7", color: "#166534"},
    muted: {background: "#F1F0EF", color: COLORS.textMuted},
  }[tone];

  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold" style={palette}>
      {children}
    </span>
  );
}

/**
 * A KPI tile. `dark` marks the single figure the page is really about; `accent` outlines
 * one supporting figure. Both are deliberately rationed — a page where everything is
 * emphasised emphasises nothing.
 */
function Tile({
  label,
  value,
  caption,
  icon: Icon,
  variant = "plain",
}: {
  label: string;
  value: React.ReactNode;
  caption?: React.ReactNode;
  icon?: React.ComponentType<{className?: string}>;
  variant?: "plain" | "dark" | "accent";
}) {
  const dark = variant === "dark";
  const accent = variant === "accent";

  return (
    <div
      className={`ds-tile ds-tile--${variant === "plain" ? "plain" : variant} rounded-2xl border p-4 flex flex-col justify-between min-h-[108px]`}
      style={{
        background: dark ? COLORS.text : COLORS.white,
        borderColor: dark ? COLORS.text : accent ? COLORS.brand : COLORS.cardBorder,
        borderWidth: accent ? 1.5 : 1,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className="text-xs font-medium"
          style={{color: dark ? "rgba(255,255,255,0.65)" : COLORS.textMuted}}
        >
          {label}
        </span>
        {Icon && (
          <Icon
            className="w-4 h-4 shrink-0"
            {...{style: {color: dark ? "rgba(255,255,255,0.65)" : COLORS.brand}} as any}
          />
        )}
      </div>
      <div>
        {/* Never wrap: a money figure broken across lines reads as two numbers. */}
        <div
          className="text-xl font-bold font-mono tracking-tight whitespace-nowrap"
          style={{color: dark ? COLORS.white : COLORS.text}}
        >
          {value}
        </div>
        {caption && (
          <div className="mt-1 text-xs" style={{color: dark ? "rgba(255,255,255,0.6)" : COLORS.textMuted}}>
            {caption}
          </div>
        )}
      </div>
    </div>
  );
}

/** A row in a parts-of-a-whole list: label, amount, share of the total as bar and percent. */
function ShareRow({label, note, value, total}: {label: string; note?: string; value: number; total: number}) {
  const percent = share(value, total);

  return (
    /*
      Stacked rather than side-by-side: this card sits in a third of the row, and a label
      competing with an amount on one line truncates the label and squeezes the note into a
      word-per-line column.
    */
    <div className="ds-row-interactive rounded-2xl p-3.5" style={{background: COLORS.surface}}>
      <div className="flex items-start justify-between gap-2">
        <p className="mb-0 text-sm font-semibold" style={{color: COLORS.text}}>{label}</p>
        <Pill tone="brand">{percentText(percent)}</Pill>
      </div>
      <p className="mb-0 mt-1.5 text-lg font-bold font-mono" style={{color: COLORS.text}}>{formatMoney(value)}</p>
      {note && <p className="mb-0 mt-0.5 text-[11px] leading-4" style={{color: COLORS.textMuted}}>{note}</p>}
      <div className="mt-2.5 h-1.5 w-full rounded-full" style={{background: "#E8E6E4"}}>
        <div
          className="h-1.5 rounded-full"
          style={{width: `${Math.min(percent, 100)}%`, background: COLORS.brand}}
        />
      </div>
    </div>
  );
}

/**
 * Daily counts as bars. One series, so no legend — the heading names it. Only the tallest
 * bar is labelled: a number over every bar is a table wearing a chart's clothes.
 */
function DayBars({rows, field, label}: {rows: any[]; field: "users" | "groupSavings"; label: string}) {
  const peak = Math.max(...rows.map((row) => Number(row[field]) || 0), 0);

  if (!rows.length) {
    return (
      <div className="flex h-44 items-center justify-center text-sm" style={{color: COLORS.textMuted}}>
        No activity recorded for this period.
      </div>
    );
  }

  return (
    <div className="flex h-44 items-end gap-2">
      {rows.map((row) => {
        const count = Number(row[field]) || 0;
        const height = peak > 0 ? Math.max((count / peak) * 100, count > 0 ? 6 : 2) : 2;
        const isPeak = count === peak && peak > 0;

        return (
          <div key={row.key} className="flex flex-1 flex-col items-center gap-2" title={`${formatDate(row.date)}: ${formatNumber(count)} ${label}`}>
            {isPeak && (
              <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{background: COLORS.brand, color: COLORS.white}}>
                {formatNumber(count)}
              </span>
            )}
            <div className="flex w-full flex-1 items-end">
              <div
                className="w-full rounded-t"
                style={{
                  height: `${height}%`,
                  background: isPeak ? COLORS.brand : "#D8D5D2",
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                }}
              />
            </div>
            <span className="text-[11px]" style={{color: COLORS.textMuted}}>{formatDate(row.date)}</span>
          </div>
        );
      })}
    </div>
  );
}

function Overview() {
  const dispatch: AppDispatch = useDispatch();
  const account = useSelector((state: RootState) => state.account);
  const transactions = useSelector((state: RootState) => state.transaction);
  const liquidation = useSelector((state: RootState) => state.liquidation);
  const groupSavings = useSelector((state: RootState) => state.groupSavings);
  const dashboard = useSelector((state: RootState) => state.dashboard);
  const dashboardCards = dashboard.cards || {};
  const hasDashboardCards = Boolean(dashboard.cards);
  const [dashboardDateRange, setDashboardDateRange] = useState<any>(null);
  const [reportMonth, setReportMonth] = useState<any>(null);
  const [exportingReport, setExportingReport] = useState(false);
  const [activitySeries, setActivitySeries] = useState<"users" | "groupSavings">("users");
  const canViewTransactions = hasAdminPermission(account.permissions, account.roles, ADMIN_PERMISSIONS.transactions);
  const canViewLiquidations = hasAdminPermission(account.permissions, account.roles, ADMIN_PERMISSIONS.groupSavingsLiquidationView);
  const canViewGroupSavings = hasAdminPermission(account.permissions, account.roles, ADMIN_PERMISSIONS.groupSavingsReports);

  const transactionSummary = transactions.summary || {
    totalCount: 0,
    totalAmount: 0,
    successfulCount: 0,
    failedCount: 0,
    pendingCount: 0,
    lencoTotalCount: 0,
    lencoTotalAmount: 0,
  };
  const liquidationSummary = liquidation.summary || {
    totalCount: 0,
    totalAmount: 0,
    pendingCount: 0,
    processingCount: 0,
    completedCount: 0,
  };
  const savingsSummary = groupSavings.summary || {
    totalCount: 0,
    activeCount: 0,
    totalBookBalance: 0,
    totalActiveBalance: 0,
  };

  const loadDashboard = useCallback(() => {
    if (!account.token) {
      return;
    }

    if (canViewTransactions) {
      dispatch(getTransactions({token: account.token, searchParam: "", pageNumber: 1}));
    }
    if (canViewLiquidations) {
      dispatch(getPaymentRequests({token: account.token, searchParam: "", pageNumber: 1}));
    }
    if (canViewGroupSavings) {
      dispatch(getGroupSavings({token: account.token, searchParam: "", pageNumber: 1}));
    }
    dispatch(getDashboardCards({
      token: account.token,
      startDate: toApiDate(dashboardDateRange?.[0]),
      endDate: toApiDate(dashboardDateRange?.[1]),
    }));
  }, [account.token, dashboardDateRange, dispatch, canViewGroupSavings, canViewLiquidations, canViewTransactions]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const downloadMonthlyReport = async () => {
    if (!account.token) {
      return;
    }

    setExportingReport(true);
    try {
      const month = reportMonth?.month?.() !== undefined ? reportMonth.month() + 1 : undefined;
      const year = reportMonth?.year?.();
      const blob = await adminDownload(`/dashboard/monthly-report/export${buildQuery({month, year})}`, account.token);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payhive-dashboard-report-${year || new Date().getFullYear()}-${String(month || new Date().getMonth() + 1).padStart(2, "0")}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success("Dashboard report downloaded");
    } catch (error: any) {
      message.error(error.message || "Could not download dashboard report");
    } finally {
      setExportingReport(false);
    }
  };

  const loading =
    (canViewTransactions && transactions.loading) ||
    (canViewLiquidations && liquidation.loading) ||
    (canViewGroupSavings && groupSavings.loading) ||
    dashboard.loading;
  const error =
    (canViewTransactions && transactions.error) ||
    (canViewLiquidations && liquidation.error) ||
    (canViewGroupSavings && groupSavings.error) ||
    dashboard.error;
  const activeUsers = getMetric(dashboardCards, "activeUsers", "ActiveUsers");
  const inactiveUsers = getMetric(dashboardCards, "inactiveUsers", "InactiveUsers");
  const totalNetAmount = getMetric(dashboardCards, "totalNetAmount", "TotalNetAmount") || transactionSummary.totalAmount;
  const totalSystemGrossAmount = getMetric(dashboardCards, "totalSystemGrossAmount", "TotalSystemGrossAmount");
  const totalCharges = getMetric(dashboardCards, "totalCharges", "TotalCharges");
  const totalPaymentLinkCharges = getMetric(dashboardCards, "totalPaymentLinkCharges", "TotalPaymentLinkCharges");
  const totalGroupSavingsAmount = hasDashboardCards ? getMetric(dashboardCards, "totalGroupSavingsAmount", "TotalGroupSavingsAmount") : savingsSummary.totalBookBalance;
  const totalGroupSavingsCharges = getMetric(dashboardCards, "totalGroupSavingsCharges", "TotalGroupSavingsCharges");
  const otherCharges = Math.max(totalCharges - totalPaymentLinkCharges - totalGroupSavingsCharges, 0);
  const totalTransactions = hasDashboardCards ? getMetric(dashboardCards, "totalTransactions", "TotalTransactions") : transactionSummary.totalCount;
  const successfulTransactions = hasDashboardCards ? getMetric(dashboardCards, "successfulTransactions", "SuccessfulTransactions") : transactionSummary.successfulCount;
  const failedTransactions = hasDashboardCards ? getMetric(dashboardCards, "failedTransactions", "FailedTransactions") : transactionSummary.failedCount;
  const pendingTransactions = hasDashboardCards ? getMetric(dashboardCards, "pendingTransactions", "PendingTransactions") : transactionSummary.pendingCount;
  const totalLiquidationRequests = hasDashboardCards ? getMetric(dashboardCards, "totalLiquidationRequests", "TotalLiquidationRequests") : liquidationSummary.totalCount;
  const totalLiquidationAmount = hasDashboardCards ? getMetric(dashboardCards, "totalLiquidationAmount", "TotalLiquidationAmount") : liquidationSummary.totalAmount;
  const processingLiquidationRequests = hasDashboardCards ? getMetric(dashboardCards, "processingLiquidationRequests", "ProcessingLiquidationRequests") : liquidationSummary.processingCount;
  const completedLiquidationRequests = hasDashboardCards ? getMetric(dashboardCards, "completedLiquidationRequests", "CompletedLiquidationRequests") : liquidationSummary.completedCount;
  const approvedLiquidationRequests = getMetric(dashboardCards, "approvedLiquidationRequests", "ApprovedLiquidationRequests");
  const totalGroupSavings = hasDashboardCards ? getMetric(dashboardCards, "totalGroupSavings", "TotalGroupSavings") : savingsSummary.totalCount;
  const activeGroupSavings = hasDashboardCards ? getMetric(dashboardCards, "activeGroupSavings", "ActiveGroupSavings") : savingsSummary.activeCount;
  const weeklyUsers = getMetric(dashboardCards, "weeklyUsers", "WeeklyUsers");
  const weeklyGroupSavings = getMetric(dashboardCards, "weeklyGroupSavings", "WeeklyGroupSavings");
  const weekStartDate = dashboardCards.reportStartDate ?? dashboardCards.ReportStartDate ?? dashboardCards.weekStartDate ?? dashboardCards.WeekStartDate;
  const weekEndDate = dashboardCards.reportEndDate ?? dashboardCards.ReportEndDate ?? dashboardCards.weekEndDate ?? dashboardCards.WeekEndDate;
  const weeklyActivity = dashboardCards.weeklyActivity ?? dashboardCards.WeeklyActivity ?? [];
  const hasDashboardDateRange = Boolean(dashboardDateRange?.[0] && dashboardDateRange?.[1]);

  const hero = splitMoney(totalNetAmount);

  const tiles = [
    {
      label: "Tracked users",
      value: formatNumber(activeUsers + inactiveUsers),
      caption: `${formatNumber(activeUsers)} active · ${formatNumber(inactiveUsers)} inactive`,
      icon: LogIn,
      variant: "plain" as const,
    },
    {
      label: "Charges earned",
      value: formatMoney(totalCharges),
      caption: "Payhive revenue from fees",
      icon: Coins,
      variant: "dark" as const,
    },
    {
      label: "Transactions",
      value: formatNumber(totalTransactions),
      caption: `${formatNumber(successfulTransactions)} successful`,
      icon: ArrowLeftRight,
      variant: "accent" as const,
    },
    {
      label: "Liquidations",
      value: formatNumber(totalLiquidationRequests),
      caption: formatMoney(totalLiquidationAmount),
      icon: Landmark,
      variant: "plain" as const,
    },
    {
      label: "Group savings",
      value: formatNumber(totalGroupSavings),
      caption: `${formatNumber(activeGroupSavings)} active`,
      icon: Users,
      variant: "plain" as const,
    },
    {
      label: hasDashboardDateRange ? "Selected range" : "This week",
      value: formatNumber(weeklyUsers),
      caption: `${formatNumber(weeklyGroupSavings)} groups joined`,
      icon: Calendar,
      variant: "plain" as const,
    },
  ];

  // Status colours sit close together for protanopia, so every segment carries a visible
  // label and the segments are separated by a surface gap rather than by hue alone.
  const statusSegments = [
    {key: "successful", label: "Successful", count: successfulTransactions, color: COLORS.success},
    {key: "pending", label: "Pending", count: pendingTransactions, color: COLORS.warning},
    {key: "failed", label: "Failed", count: failedTransactions, color: COLORS.danger},
  ];
  const statusTotal = statusSegments.reduce((sum, segment) => sum + (Number(segment.count) || 0), 0);

  const earningsRows = [
    {
      key: "payment-link-charges",
      label: "Payment link charges",
      value: totalPaymentLinkCharges,
      note: "Revenue from payment link collections.",
    },
    {
      key: "group-savings-charges",
      label: "Group savings charges",
      value: totalGroupSavingsCharges,
      note: "Revenue from group savings contributions.",
    },
    {
      key: "other-charges",
      label: "Other transaction charges",
      value: otherCharges,
      note: "Remaining charges from other payment activity.",
    },
  ];

  const activityRows = [
    {
      key: "successful-transactions",
      metric: "Successful transactions",
      count: successfulTransactions,
      value: formatMoney(totalNetAmount),
      status: "Transactions",
    },
    {
      key: "pending-transactions",
      metric: "Pending transactions",
      count: pendingTransactions,
      value: `${formatNumber(failedTransactions)} failed`,
      status: "Transactions",
    },
    {
      key: "approved-liquidations",
      metric: "Approved liquidations",
      count: approvedLiquidationRequests,
      value: formatMoney(totalLiquidationAmount),
      status: "Liquidation",
    },
    {
      key: "processing-liquidations",
      metric: "Processing liquidations",
      count: processingLiquidationRequests,
      value: formatMoney(totalLiquidationAmount),
      status: "Liquidation",
    },
    {
      key: "completed-liquidations",
      metric: "Completed liquidations",
      count: completedLiquidationRequests,
      value: formatMoney(totalLiquidationAmount),
      status: "Liquidation",
    },
    {
      key: "active-savings",
      metric: "Active savings groups",
      count: activeGroupSavings,
      value: formatMoney(totalGroupSavingsAmount),
      status: "Savings",
    },
  ];

  const weeklyRows = weeklyActivity.map((row: any, index: number) => ({
    key: row.date ?? row.Date ?? index,
    date: row.date ?? row.Date,
    users: row.users ?? row.Users ?? 0,
    groupSavings: row.groupSavings ?? row.GroupSavings ?? 0,
  }));

  const weeklyTotal = weeklyRows.reduce(
    (sum: number, row: any) => sum + (Number(row[activitySeries]) || 0),
    0,
  );
  const weeklyAverage = weeklyRows.length ? weeklyTotal / weeklyRows.length : 0;

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        {/*
          No page title here — the app shell already prints "Overview" above this. The hero
          figure below carries the display type instead.
        */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="mb-0 text-sm" style={{color: COLORS.textMuted}}>
            Logins, transactions, liquidation pressure and group savings exposure.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <div
              className="flex items-center gap-2 rounded-full border px-2 py-1"
              style={{background: COLORS.white, borderColor: COLORS.cardBorder}}
            >
              <span className="pl-2 text-xs font-medium" style={{color: COLORS.textMuted}}>Timeframe</span>
              <RangePicker
                value={dashboardDateRange}
                onChange={(value) => setDashboardDateRange(value)}
                allowClear
                disabled={loading}
                variant="borderless"
                size="small"
              />
            </div>
            <button
              onClick={loadDashboard}
              disabled={loading}
              title="Refresh"
              className="flex h-10 w-10 items-center justify-center rounded-full border cursor-pointer disabled:opacity-60"
              style={{background: COLORS.white, borderColor: COLORS.cardBorder, color: COLORS.text}}
            >
              <ReloadOutlined spin={loading}/>
            </button>
          </div>
        </div>

        {error && (
          <Alert
            className="mt-6 rounded-2xl"
            type="warning"
            showIcon
            title="Some dashboard data could not be refreshed"
            description={error}
          />
        )}

        {/* Hero figure beside the KPI tiles. */}
        <section className="mt-7 grid grid-cols-1 gap-4 xl:grid-cols-12">
          <Card className="p-6 xl:col-span-5">
            <p className="mb-2 text-sm font-semibold" style={{color: COLORS.text}}>Net transaction value</p>
            <div className="flex items-end gap-2">
              <span className="text-4xl md:text-5xl font-bold font-mono tracking-tight" style={{color: COLORS.text}}>
                {hero.whole}
              </span>
              <span className="pb-1 text-2xl font-bold font-mono" style={{color: "#C9C5C1"}}>.{hero.fraction}</span>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Pill tone="brand">Gross {formatMoney(totalSystemGrossAmount)}</Pill>
              <Pill tone="success">Charges {formatMoney(totalCharges)}</Pill>
            </div>

            <p className="mb-0 mt-4 text-sm" style={{color: COLORS.textMuted}}>
              Across {formatNumber(totalTransactions)} transactions
              {hasDashboardDateRange ? " in the selected range" : " to date"}.
            </p>
          </Card>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:col-span-7">
            {tiles.map((tile) => (
              <Tile
                key={tile.label}
                label={tile.label}
                value={tile.value}
                caption={tile.caption}
                icon={tile.icon}
                variant={tile.variant}
              />
            ))}
          </div>
        </section>

        {/* Transaction status as one segmented bar. */}
        <Card className="mt-4 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              {statusSegments.map((segment) => {
                const percent = share(Number(segment.count) || 0, statusTotal);
                return (
                  <div
                    key={segment.key}
                    className="flex min-w-[180px] flex-1 items-center justify-between gap-3 rounded-full border px-3 py-2"
                    style={{background: COLORS.white, borderColor: COLORS.cardBorder}}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium" style={{color: COLORS.text}}>
                      <span className="h-2.5 w-2.5 rounded-full" style={{background: segment.color}}/>
                      {segment.label}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-bold font-mono" style={{color: COLORS.text}}>
                        {formatNumber(segment.count)}
                      </span>
                      <span className="text-xs" style={{color: COLORS.textMuted}}>{percentText(percent)}</span>
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              <DatePicker
                picker="month"
                value={reportMonth}
                onChange={setReportMonth}
                placeholder="Report month"
                disabled={exportingReport}
              />
              <button
                onClick={downloadMonthlyReport}
                disabled={exportingReport}
                className="inline-flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold text-white disabled:opacity-70"
                style={{background: COLORS.text}}
              >
                <DownloadOutlined spin={exportingReport}/> Report
              </button>
            </div>
          </div>
        </Card>

        <section className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
          {/* Where the fee revenue comes from. */}
          <Card className="p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="mb-0.5 text-base font-semibold" style={{color: COLORS.text}}>Earnings breakdown</h3>
                <p className="mb-0 text-xs" style={{color: COLORS.textMuted}}>Charges are Payhive profit</p>
              </div>
              <Pill tone="neutral">{formatMoney(totalCharges)}</Pill>
            </div>

            <div className="space-y-2.5">
              {earningsRows.map((row) => (
                <ShareRow key={row.key} label={row.label} note={row.note} value={row.value} total={totalCharges}/>
              ))}
            </div>

            <div className="mt-4 rounded-2xl p-4" style={{background: COLORS.brandTint}}>
              <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold" style={{color: COLORS.brand}}>
                <Clock className="w-4 h-4"/>
                Cash planning note
              </div>
              <p className="mb-0 text-xs leading-5" style={{color: COLORS.textMuted}}>
                Use charges earned for Payhive budgeting decisions. Customer balances and savings books remain
                separate operational funds.
              </p>
            </div>
          </Card>

          {/* Operational counts, as rows rather than a grid of borders. */}
          <Card className="p-5 xl:col-span-2">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="mb-0.5 text-base font-semibold" style={{color: COLORS.text}}>Activity snapshot</h3>
                <p className="mb-0 text-xs" style={{color: COLORS.textMuted}}>
                  Counts and value from the currently loaded operational data.
                </p>
              </div>
              {loading && <Spin size="small"/>}
            </div>

            <div className="space-y-2">
              {activityRows.map((row) => (
                <div
                  key={row.key}
                  className="ds-row-interactive flex flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3"
                  style={{background: COLORS.surface}}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Pill tone="muted">{row.status}</Pill>
                    <span className="truncate text-sm font-medium" style={{color: COLORS.text}}>{row.metric}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-flex min-w-[44px] justify-center rounded-full px-2.5 py-1 text-xs font-bold font-mono text-white"
                      style={{background: COLORS.text}}
                    >
                      {formatNumber(row.count)}
                    </span>
                    <span className="text-sm font-semibold font-mono" style={{color: COLORS.textMuted}}>{row.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </section>

        {/* Weekly joins: period summary in the accent panel, daily shape beside it. */}
        <Card className="mt-4 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
            <div className="p-6 text-white" style={{background: COLORS.brand}}>
              <p className="mb-5 text-xs font-medium" style={{color: "rgba(255,255,255,0.7)"}}>
                {formatDate(weekStartDate)} – {formatDate(weekEndDate)}
              </p>
              <div className="space-y-4">
                <div>
                  <p className="mb-0.5 text-xs" style={{color: "rgba(255,255,255,0.7)"}}>Users joined</p>
                  <p className="mb-0 text-2xl font-bold font-mono">{formatNumber(weeklyUsers)}</p>
                </div>
                <div>
                  <p className="mb-0.5 text-xs" style={{color: "rgba(255,255,255,0.7)"}}>Groups created</p>
                  <p className="mb-0 text-2xl font-bold font-mono">{formatNumber(weeklyGroupSavings)}</p>
                </div>
                <div>
                  <p className="mb-0.5 text-xs" style={{color: "rgba(255,255,255,0.7)"}}>Daily average</p>
                  <p className="mb-0 text-2xl font-bold font-mono">
                    {weeklyAverage.toFixed(weeklyAverage >= 10 ? 0 : 1)}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="mb-0.5 text-base font-semibold" style={{color: COLORS.text}}>Weekly join report</h3>
                  <p className="mb-0 text-xs" style={{color: COLORS.textMuted}}>
                    {activitySeries === "users" ? "Users" : "Groups"} created per day
                  </p>
                </div>

                <div className="inline-flex rounded-full p-1" style={{background: COLORS.surface}}>
                  {([
                    {key: "users", label: "Users"},
                    {key: "groupSavings", label: "Groups"},
                  ] as const).map((option) => {
                    const active = activitySeries === option.key;
                    return (
                      <button
                        key={option.key}
                        onClick={() => setActivitySeries(option.key)}
                        className="rounded-full px-4 py-1.5 text-sm font-semibold border-0 cursor-pointer"
                        style={{
                          background: active ? COLORS.text : "transparent",
                          color: active ? COLORS.white : COLORS.textMuted,
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <DayBars
                rows={weeklyRows}
                field={activitySeries}
                label={activitySeries === "users" ? "users" : "groups"}
              />
            </div>
          </div>
        </Card>
      </main>
    </AdminLayout>
  );
}

export default Overview;
